import { request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

export const MARKET_DETAIL_API_PATH = '/api/v4/bit/market/detail'

export type MarketDetailParams = {
  module: string
  lang: string
}

export type MarketDetailOptions = {
  basicData?: string
  forwardedFor?: string
}

export type MarketDetailResponse = Record<string, any> | null

export type MarketDetail = {
  share?: MarketDetailShare // 公共分享
  popup?: MarketDetailPopup // 公共弹窗
  summary?: MarketDetailSummary // 市场概览
  fearGreed?: MarketDetailFearGreed // 恐惧贪婪指数
  top5?: MarketDetailTop5Item[] // 市值 Top5 行情
  cryptoEtf?: MarketDetailEtfPeriod[] // BTC/ETH ETF 净流入
}

export type MarketDetailShare = {
  title?: string // 分享标题
  desc?: string // 分享描述
  link?: string // 分享链接
  img?: string // 分享图
}

export type MarketDetailPopupSection = {
  key?: string // 区块标识
  title?: string // 区块标题
  content?: string // 区块说明内容
}

export type MarketDetailPopup = {
  module?: string // 模块名
  sections?: MarketDetailPopupSection[] // 说明区块列表
}

export type MarketDetailMonetaryValue = {
  value?: string // 展示文案
  raw?: number // 原始数值
  currency?: string // 币种
  unit?: string // 单位
  previousValue?: string // 上期展示文案
  previousRaw?: number // 上期原始数值
}

export type OpenInterest = {
  type?: number
  value?: MarketDetailMonetaryValue
  ratio?: MarketDetailMonetaryValue
}

export type MarketDetailDominanceItem = {
  symbol?: string // 币种符号
  icon?: string // 币种图标
  dominancePercentage?: MarketDetailMonetaryValue // 市场占比
}

export type MarketDetailVolmexHistoryPoint = {
  timestamp?: number // 时间戳(秒)
  value?: number // 波动率值
}

export type MarketDetailVolmexItem = {
  symbol?: string // 标的名称
  value?: number // 30天隐含波动率
  history?: MarketDetailVolmexHistoryPoint[] // 历史趋势
}

export type MarketDetailGasTier = {
  value?: number // Gas 数值(Gwei)
  display?: string // 展示文案
}

export type MarketDetailGas = {
  safe?: MarketDetailGasTier // 安全档
  propose?: MarketDetailGasTier // 标准档
  fast?: MarketDetailGasTier // 快速档
}

export type MarketDetailSummary = {
  updateTime?: number // 数据更新时间戳(秒)
  totalMarketCapUsd?: MarketDetailMonetaryValue // 全球总市值
  totalMarketCapUsdRatio?: MarketDetailMonetaryValue // 总市值24h涨跌幅
  totalVolume24hUsd?: MarketDetailMonetaryValue // 24h总交易量
  totalVolume24hUsdRatio?: MarketDetailMonetaryValue // 24h交易量涨跌幅
  marketDominance?: MarketDetailDominanceItem[] // 市场占比列表
  openInterestProxyUsd?: MarketDetailMonetaryValue // 全市场未平仓合约
  openInterestProxyUsdRatio?: MarketDetailMonetaryValue // 未平仓合约24h涨跌幅
  openInterestItems: OpenInterest[] // 永续合约 + 期货
  cryptoSeasonIndex?: number // 山寨季指数
  volmexVolatility?: MarketDetailVolmexItem[] // Volmex隐含波动率
  gas?: MarketDetailGas // 以太坊 Gas
}

export type MarketDetailFearGreedItem = {
  pointTime?: string // 数据点时间
  pointTimestamp?: number // 时间戳(秒)
  indexValue?: number // 指数值
  indexLabel?: number // 情绪标签
  indexLabelText?: string // 情绪标签文本
}

export type MarketDetailFearGreed = {
  currentIndexValue?: number // 当前指数值
  currentIndexLabel?: number // 当前情绪标签
  currentIndexLabelText?: string // 当前情绪标签文本
  previousIndexValue?: number // 上一周期指数值
  items?: MarketDetailFearGreedItem[] // 历史曲线数据点
}

export type MarketDetailTop5Item = {
  marketId?: string // 币种id
  coinSymbol?: string // 币种符号
  coinName?: string // 币种名称
  coinLogo?: string // 币种 logo
  price?: MarketDetailMonetaryValue // 当前价格
  change24h?: MarketDetailMonetaryValue // 24h涨跌幅
  trend?: MarketSparkline[] // 折线图数据
}

export type MarketDetailEtfSummaryItem = {
  symbol?: string // ETF 币种
  totalNetFlow?: string // 周期净流入总额
  totalNetFlowValue?: number // 周期净流入总额数值
}

export type MarketDetailEtfFlowItem = {
  name?: string // ETF 名称
  flow?: string // 当日净流入
  flowValue?: number // 当日净流入数值
}

export type MarketDetailEtfSeriesPoint = {
  timestamp?: number // 日期时间戳(秒)
  items?: MarketDetailEtfFlowItem[] // 当日各 ETF 净流入
}

export type MarketDetailEtfPeriod = {
  period?: string // 周期: 30d / 1y / all
  summary?: MarketDetailEtfSummaryItem[] // 周期净流入汇总
  series?: MarketDetailEtfSeriesPoint[] // 时序数据
}

// 折线图数据
export type MarketSparkline = {
  displayValue?: string // 显示数值
  value?: number // 数值
  timestamp?: number // 时间戳
}

export type MarketSummaryCard = {
  title: string
  value: string
  change: string
  trend: 'down' | 'up' | ''
}

export type TopCrypto = {
  marketId: string
  symbol: string
  price: string
  change: string
  trend: 'down' | 'up'
  icon: string
  trendSeries: number[]
}

export type DominanceItem = {
  color: string
  icon: string
  value: string
  percent: number
}

export type GasFeeItem = {
  label: string
  value: string
  time: string
}

// 鑾峰彇琛屾儏甯傚満璇︽儏鏁版嵁
export const getMarketDetail = (
  data: MarketDetailParams,
  options: MarketDetailOptions = {},
): Promise<MarketDetailResponse> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.wikibitBffGo : api.v4,
    url: MARKET_DETAIL_API_PATH,
    method: 'GET' as const,
    params: data,
    headers: {
      lang: data.lang,
      'X-Forwarded-For': options.forwardedFor || '114.114.114.114',
      'Content-Type': 'application/json',
    },
  }

  return import.meta.server ? useServerFetch<MarketDetailResponse>(requestConfig) : request(requestConfig)
}
